import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import IosShareIcon from '@mui/icons-material/IosShare';
import {
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  Grid,
  Grow,
  MenuItem,
  MenuList,
  Modal,
  Paper,
  Popper,
  TableContainer,
} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import PlusIcon from '@untitled-ui/icons-react/build/esm/Plus';
import UploadIcon from '@untitled-ui/icons-react/build/esm/Upload01';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DropzoneArea } from 'react-mui-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import LoaderIcon from 'src/components/LoaderImg/loader.gif';
import { MultiSelect } from 'src/components/multi-select';
import { Seo } from 'src/components/seo';
import { useSettings } from 'src/hooks/use-settings';
import useEffectAfterMount from 'src/layouts/components/useEffectAfterMount';
import emptyImg from 'src/pages/therapist-dashboard/therapist-document/image-removebg-preview.png';
import { paths } from 'src/paths';
import { Permissions, useScopeChecker } from 'src/rbac';
import { getAllSubmittedDocumentReset } from 'src/redux/actions/documentationAction';
import {
  getAllPatientsListAction,
  getAllPatientsListResetAction,
  getDownloadCSVPatientTemplate,
  getEnterprisePayerPlanType,
  getExportPatientTemplate,
  getPatientActiveInActiveCount,
  getPatientIdAction,
  getRecentUploadHistoryPatients,
  getValidationCSVTemplate,
  persistPatientIdAction,
  persistPatientListCriteria,
  persistPatientStatusFilter,
  persistPatientActiveCert,
  resetExportPatientTemplate,
  resetValidationCSVTemplate,
  getAllPatientPriorChargesAction,
} from 'src/redux/actions/patientAction';
import Search from 'src/sections/dashboard/patients/patient-listing-search';
import ListingTable from 'src/sections/dashboard/patients/patient-listing-table';
import { patientsTestId } from 'src/sections/dashboard/patients/test-ids';
import SimpleSnackbar from './details-page/sub-components/snackBar';

const Patients = () => {
  const settings = useSettings();
  const [page, setPage] = useState(0);
  const has = useScopeChecker();

  const getAllPatientRes = useSelector((state: any) => state.getAllPatientListReducer);

  const persistedStatusFilter = getAllPatientRes?.statusFilter || { active: true, inactive: false };
  const persistedActiveCert = getAllPatientRes?.activeCert || { PT: false, OT: false, SLP: false };

  const [activeChecked, setActiveChecked] = useState(() => persistedStatusFilter.active);
  const [inactiveChecked, setInactiveChecked] = useState(() => persistedStatusFilter.inactive);
  const [ptChecked, setPtChecked] = useState(() => persistedActiveCert.PT);
  const [otChecked, setOtChecked] = useState(() => persistedActiveCert.OT);
  const [slpChecked, setSlpChecked] = useState(() => persistedActiveCert.SLP);

  const activeCheckedRef = useRef(persistedStatusFilter.active);
  const inactiveCheckedRef = useRef(persistedStatusFilter.inactive);
  const ptCheckedRef = useRef(persistedActiveCert.PT);
  const otCheckedRef = useRef(persistedActiveCert.OT);
  const slpCheckedRef = useRef(persistedActiveCert.SLP);

  useEffect(() => {
    setActiveChecked(persistedStatusFilter.active);
    setInactiveChecked(persistedStatusFilter.inactive);
    setPtChecked(persistedActiveCert.PT);
    setOtChecked(persistedActiveCert.OT);
    setSlpChecked(persistedActiveCert.SLP);
    activeCheckedRef.current = persistedStatusFilter.active;
    inactiveCheckedRef.current = persistedStatusFilter.inactive;
    ptCheckedRef.current = persistedActiveCert.PT;
    otCheckedRef.current = persistedActiveCert.OT;
    slpCheckedRef.current = persistedActiveCert.SLP;
  }, [
    persistedStatusFilter.active,
    persistedStatusFilter.inactive,
    persistedActiveCert.PT,
    persistedActiveCert.OT,
    persistedActiveCert.SLP,
  ]);

  useEffect(() => {
    activeCheckedRef.current = activeChecked;
  }, [activeChecked]);

  useEffect(() => {
    inactiveCheckedRef.current = inactiveChecked;
  }, [inactiveChecked]);

  useEffect(() => {
    ptCheckedRef.current = ptChecked;
  }, [ptChecked]);

  useEffect(() => {
    otCheckedRef.current = otChecked;
  }, [otChecked]);

  useEffect(() => {
    slpCheckedRef.current = slpChecked;
  }, [slpChecked]);

  const [searchData, setSearchData] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { patients, patientDetails, createPatient, episodeOfCare, details } = paths;
  const [popUpContent, setPopup] = useState(false);
  const [uploadCsvModal, setUploadCsvModal] = useState(false);
  const anchorRef = useRef<any>(null);
  const [fileUploaded, setFileUploaded] = useState<any>();
  const getPayerPlanTypeResponse = useSelector(
    (state: any) => state.getPayerPlanTypeReducer?.enterprisePayerPlanType?.data
  );
  const facilityId = localStorage.getItem('facilityId');

  const getPatientCount = useSelector(
    (state: any) => state.getPatientActiveInActiveCountReducer?.response
  );
  const getDownloadCSVResponse = useSelector(
    (state: any) => state.getDownloadCSVTemplateReducer?.response
  );
  const getPatientExportCsvRespose = useSelector((state: any) => state.getPatientExportCsvReducer);
  const [patientError, setPatientError] = useState('');

  const [selectedPayerPlanValues, setSelectedPayerPlanValues] = useState<string[]>([]);
  useEffect(() => {
    dispatch(getEnterprisePayerPlanType());
    dispatch(
      getPatientActiveInActiveCount({
        facilityId: facilityId,
      })
    );
    dispatch(resetValidationCSVTemplate());
  }, []);

  useEffect(() => {
    if (!getPatientExportCsvRespose.isExportSuccess && getPatientExportCsvRespose.response === null)
      return;
    if (
      !getPatientExportCsvRespose.isExportSuccess &&
      getPatientExportCsvRespose.response !== null
    ) {
      setPatientError(getPatientExportCsvRespose?.response?.message);
      return;
    }
    if (getPatientExportCsvRespose?.isExportSuccess) {
      setPatientError('');
      downloadFile(getPatientExportCsvRespose?.response?.data);
    }
    setTimeout(() => {
      dispatch(resetExportPatientTemplate());
    }, 3000);
    return;
  }, [getPatientExportCsvRespose]);

  const getAllPatientApiCall = (page: any) => {
    let activeStatus: boolean | null | '' = '';
    if (activeChecked && inactiveChecked) {
      activeStatus = null;
    } else if (activeChecked) {
      activeStatus = true;
    } else if (inactiveChecked) {
      activeStatus = false;
    }

    // Build comma-separated string for selected disciplines
    const selectedDisciplines = [];
    if (ptChecked) selectedDisciplines.push('PT');
    if (otChecked) selectedDisciplines.push('OT');
    if (slpChecked) selectedDisciplines.push('SLP');
    const activeCertFlag = selectedDisciplines.join(',');

    dispatch(
      getAllPatientsListAction({
        data: {
          searchString: searchData?.trim()?.split('-')?.join(''),
        },
        params: {
          page,
          pageSize: 20,
          active: activeStatus,
          payerType: selectedPayerPlanValues.length > 0 ? selectedPayerPlanValues[0] : '',
          activeDisciplines: activeCertFlag,
        },
      })
    );
  };

  useEffect(() => {
    if (getAllPatientRes?.totalPatient?.length && !getAllPatientRes?.searchName) {
      return;
    }
    dispatch(getAllPatientsListResetAction());
    getAllPatientApiCall(0);
  }, []);

  useEffectAfterMount(() => {
    dispatch(getAllPatientsListResetAction());
    getAllPatientApiCall(0);
  }, [
    activeChecked,
    inactiveChecked,
    searchData,
    selectedPayerPlanValues,
    ptChecked,
    otChecked,
    slpChecked,
  ]);

  const onRowClick = (data: any) => {
    if (!has(Permissions.READ_EOC)) {
      return;
    }
    dispatch(persistPatientIdAction(data?.uuid));
    dispatch(
      persistPatientListCriteria({
        searchName: searchData,
        payerType: '',
        status: '',
      })
    );
    dispatch(getAllSubmittedDocumentReset());
    dispatch(getPatientIdAction(data?.uuid));
    dispatch(getAllPatientPriorChargesAction(data?.uuid));
    navigate(`${patients}/${patientDetails}${episodeOfCare}`);
  };

  const handleAddPatientPopUp = useCallback((): void => {
    setPopup((prevOpen) => !prevOpen);
  }, []);

  function addNewPatient() {
    navigate(`${patients}/${createPatient}`);
  }

  const handleUploadCsvModal = () => {
    setUploadCsvModal(true);
  };
  const handleCloseUploadCsvModal = () => {
    setUploadCsvModal(false);
  };

  const handleActiveCheckboxChange = useCallback(
    (checked: boolean) => {
      setActiveChecked(checked);
      activeCheckedRef.current = checked;
      dispatch(
        persistPatientStatusFilter({ active: checked, inactive: inactiveCheckedRef.current })
      );
    },
    [dispatch]
  );

  const handleInactiveCheckboxChange = useCallback(
    (checked: boolean) => {
      setInactiveChecked(checked);
      inactiveCheckedRef.current = checked;
      dispatch(persistPatientStatusFilter({ active: activeCheckedRef.current, inactive: checked }));
    },
    [dispatch]
  );

  const handlePtCheckboxChange = useCallback(
    (checked: boolean) => {
      setPtChecked(checked);
      ptCheckedRef.current = checked;
      dispatch(
        persistPatientActiveCert({
          PT: checked,
          OT: otCheckedRef.current,
          SLP: slpCheckedRef.current,
        })
      );
    },
    [dispatch]
  );

  const handleOtCheckboxChange = useCallback(
    (checked: boolean) => {
      setOtChecked(checked);
      otCheckedRef.current = checked;
      dispatch(
        persistPatientActiveCert({
          PT: ptCheckedRef.current,
          OT: checked,
          SLP: slpCheckedRef.current,
        })
      );
    },
    [dispatch]
  );

  const handleSlpCheckboxChange = useCallback(
    (checked: boolean) => {
      setSlpChecked(checked);
      slpCheckedRef.current = checked;
      dispatch(
        persistPatientActiveCert({
          PT: ptCheckedRef.current,
          OT: otCheckedRef.current,
          SLP: checked,
        })
      );
    },
    [dispatch]
  );

  const payerPlanOptions = useMemo(() => {
    return getPayerPlanTypeResponse?.map((option: any) => ({
      label: option?.displayName,
      value: option?.key,
    }));
  }, [getPayerPlanTypeResponse]);

  const handleClose = useCallback((event: Event): void => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setPopup(false);
  }, []);

  const handleDownloadCSVTemplate = () => {
    dispatch(getDownloadCSVPatientTemplate());
    downloadFile(getDownloadCSVResponse);
  };

  const downloadFile = (response: any) => {
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'patient-template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleUploadValidationTemplate = () => {
    const formData = new FormData();

    formData.append('file', fileUploaded[0]);

    dispatch(getValidationCSVTemplate(formData));
    navigate(`${patients}/upload`);
  };

  const handleRecentUpdate = () => {
    dispatch(getRecentUploadHistoryPatients());
    navigate(`${patients}/recent-uploaded`);
  };
  const handleExportFile = () => {
    dispatch(
      getExportPatientTemplate({
        searchString: '',
      })
    );

    handleClose;
  };

  // The list grows the page instead of scrolling in its own box (BRK-2148), so infinite scroll
  // watches the window. The ref keeps the listener on the latest render's state.
  const handleScrollRef = useRef<() => void>(() => {});

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    if (scrollTop + clientHeight >= scrollHeight - 10 && !getAllPatientRes?.loading) {
      if (
        getAllPatientRes?.response?.data?.totalElements !== getAllPatientRes?.totalPatient?.length
      ) {
        getAllPatientApiCall(getAllPatientRes?.pageNumber);
      }
    }
  };
  handleScrollRef.current = handleScroll;

  useEffect(() => {
    const onScroll = () => handleScrollRef.current();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // The filter row sticks under the 64px top nav; the table's column headers stick under it.
  const TOP_NAV_HEIGHT = 64;
  const filterBarRef = useRef<HTMLDivElement>(null);
  const [filterBarHeight, setFilterBarHeight] = useState(0);

  useEffect(() => {
    const el = filterBarRef.current;
    if (!el) return;
    setFilterBarHeight(el.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setFilterBarHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Seo title="Restore-Insights: Patient" />

      <Container maxWidth={settings.stretch ? false : 'xl'}>
        <Stack
          spacing={{
            xs: 1,
            lg: 1,
          }}
          mt={2}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={4}
          >
            <Stack
              ml={4}
              gap="1rem"
              flexDirection="row"
              alignItems={'center'}
            >
              <Stack>
                <Typography variant="h4">Patients</Typography>
              </Stack>
              <Stack
                gap="1rem"
                flexDirection="row"
                alignItems={'center'}
              >
                <Stack
                  flexDirection="row"
                  sx={{
                    borderRadius: '12px',
                    display: 'flex',
                    border: '1px solid #00000042',
                    color: '#111927',
                    padding: '3px 10px',
                  }}
                >
                  {getPatientCount?.allCount <= 1
                    ? `${getPatientCount?.allCount} Patient`
                    : `${getPatientCount?.allCount} Patients`}
                </Stack>

                <Stack
                  flexDirection="row"
                  sx={{
                    borderRadius: '12px',
                    display: 'flex',
                    border: '1px solid #15B79E80',

                    color: '#15B79E',
                    padding: '3px 10px',
                    opacity: '.7',
                  }}
                >
                  {getPatientCount?.activeCount ? getPatientCount?.activeCount : '0'} Active
                </Stack>
                <Stack
                  flexDirection="row"
                  sx={{
                    borderRadius: '12px',
                    display: 'flex',
                    border: '1px solid #F7900980',
                    color: '#F79009',
                    padding: '3px 10px',
                  }}
                >
                  {getPatientCount?.deActiveCount ? getPatientCount?.deActiveCount : '0'} Inactive
                </Stack>
              </Stack>
            </Stack>

            <Stack
              direction="row"
              spacing={4}
            >
              <Button
                startIcon={
                  <SvgIcon>
                    <PlusIcon />
                  </SvgIcon>
                }
                endIcon={
                  <SvgIcon>
                    <ExpandMoreIcon />
                  </SvgIcon>
                }
                // sx={{ position: 'absolute', right: 0 }}
                variant="contained"
                data-testid={patientsTestId('add-patient-menu')}
                disabled={!has(Permissions.CREATE_PATIENT)}
                onClick={handleAddPatientPopUp}
                ref={anchorRef}
              >
                Add New Patient
              </Button>
              <Popper
                anchorEl={anchorRef.current}
                open={popUpContent}
                transition
                // Portalled to body with no z-index, the menu sat under the sticky filter row
                // (BRK-2148). appBar keeps it over the page and top nav but under modals.
                sx={{ zIndex: (theme) => theme.zIndex.appBar }}
              >
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                      boxShadow: '0px 3px 14px 0px rgba(0, 0, 0, 0.08)',
                      borderRadius: '12px',
                      background: '#fff',
                      // width: 'auto',
                    }}
                  >
                    <Paper>
                      <ClickAwayListener onClickAway={handleClose}>
                        <MenuList>
                          <MenuItem
                            data-testid={patientsTestId('add-patient')}
                            onClick={addNewPatient}
                          >
                            <Grid container>
                              <Grid item>
                                <SvgIcon>
                                  <PlusIcon />
                                </SvgIcon>
                              </Grid>
                              <Grid item>Add New Patient</Grid>
                            </Grid>
                          </MenuItem>

                          <MenuItem
                            data-testid={patientsTestId('upload-csv')}
                            onClick={handleUploadCsvModal}
                          >
                            <Grid container>
                              <Grid
                                item
                                marginRight={0.5}
                              >
                                <SvgIcon>
                                  <UploadIcon />
                                </SvgIcon>
                              </Grid>
                              <Grid item>Upload CSV File</Grid>
                            </Grid>
                          </MenuItem>
                          <MenuItem
                            data-testid={patientsTestId('uploads-history')}
                            onClick={handleRecentUpdate}
                          >
                            <Grid container>
                              <Grid
                                item
                                marginRight={0.5}
                              >
                                <SvgIcon>
                                  <HistoryIcon />
                                </SvgIcon>
                              </Grid>
                              <Grid item>View Uploads History</Grid>
                            </Grid>
                          </MenuItem>
                          <MenuItem
                            data-testid={patientsTestId('download-list')}
                            onClick={handleExportFile}
                          >
                            <Grid container>
                              <Grid
                                item
                                marginRight={0.5}
                              >
                                <SvgIcon>
                                  <IosShareIcon />
                                </SvgIcon>
                              </Grid>
                              <Grid item>Download Patients List</Grid>
                            </Grid>
                          </MenuItem>
                        </MenuList>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </Stack>
          </Stack>
          {/* overflow: visible -- Card's default overflow: hidden would stop the sticky rows below */}
          <Card sx={{ padding: '2rem', overflow: 'visible' }}>
            <Stack
              ref={filterBarRef}
              flexDirection={'row'}
              gap={'35px'}
              sx={{
                position: 'sticky',
                top: TOP_NAV_HEIGHT,
                zIndex: 3,
                // Breathing room under the nav when stuck; the negative margin keeps the resting
                // layout unchanged by pulling the padding into the Card's own top padding.
                pt: '1rem',
                mt: '-1rem',
                backgroundColor: 'background.paper',
              }}
            >
              <Stack
                sx={{
                  maxWidth: 550,
                  flex: 1,
                  minWidth: 230,
                }}
              >
                <Search
                  testId={patientsTestId('search')}
                  setSearchData={setSearchData}
                  searchData={searchData}
                  setPage={setPage}
                  placeholder="Search by patient name, medical record no, social security number, etc."
                />
              </Stack>
              <Stack
                gap="1rem"
                sx={{ padding: '0 1rem 1rem 1rem' }}
                flexDirection="row"
                alignItems="center"
              >
                <Stack
                  flexDirection="row"
                  alignItems="center"
                  gap="0.5rem"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{
                          padding: '3px',
                        }}
                        size="small"
                        checked={activeChecked}
                        inputProps={
                          {
                            'data-testid': patientsTestId('filter-active'),
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        onChange={(e) => handleActiveCheckboxChange(e.target.checked)}
                      />
                    }
                    label="Active"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{
                          padding: '3px',
                        }}
                        checked={inactiveChecked}
                        inputProps={
                          {
                            'data-testid': patientsTestId('filter-inactive'),
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        onChange={(e) => handleInactiveCheckboxChange(e.target.checked)}
                      />
                    }
                    label="Inactive"
                  />
                </Stack>
                <MultiSelect
                  label="Payer Type"
                  isMulti={false}
                  options={payerPlanOptions || []}
                  value={selectedPayerPlanValues}
                  onChange={(values: string[]) => {
                    setSelectedPayerPlanValues(values);
                    const selectedKeys = values?.map((value) => {
                      return value;
                    });
                  }}
                />

                <Stack
                  flexDirection="row"
                  alignItems="center"
                  gap="0.5rem"
                  paddingLeft={3}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{
                          padding: '3px',
                        }}
                        size="small"
                        checked={ptChecked}
                        inputProps={
                          {
                            'data-testid': patientsTestId('filter-pt'),
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        onChange={(e) => handlePtCheckboxChange(e.target.checked)}
                      />
                    }
                    label="PT"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{
                          padding: '3px',
                        }}
                        size="small"
                        checked={otChecked}
                        inputProps={
                          {
                            'data-testid': patientsTestId('filter-ot'),
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        onChange={(e) => handleOtCheckboxChange(e.target.checked)}
                      />
                    }
                    label="OT"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{
                          padding: '3px',
                        }}
                        size="small"
                        checked={slpChecked}
                        inputProps={
                          {
                            'data-testid': patientsTestId('filter-slp'),
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        onChange={(e) => handleSlpCheckboxChange(e.target.checked)}
                      />
                    }
                    label="SLP"
                  />
                </Stack>
              </Stack>
            </Stack>

            <Stack gap="1rem">
              <TableContainer
                component={Paper}
                sx={{
                  // TableContainer's overflow-x: auto would make it the sticky header's
                  // scroll container; visible lets the headers stick to the page instead.
                  overflow: 'visible',
                  '& .MuiTableCell-stickyHeader': {
                    top: TOP_NAV_HEIGHT + filterBarHeight,
                  },
                }}
              >
                <Box>
                  {getAllPatientRes?.totalPatient?.length === 0 && !getAllPatientRes?.loading ? (
                    <Box>
                      <img
                        src={emptyImg}
                        alt="loader"
                        style={{
                          width: 720,
                          height: 300,
                          margin: 'auto',
                          alignItems: 'center',
                          alignContent: 'center',
                          marginTop: 100,
                          display: 'block',
                        }}
                      />

                      <Typography
                        color={'text.secondary'}
                        variant="h5"
                        fontWeight={'200'}
                        sx={{ display: 'flex', justifyContent: 'center' }}
                      >
                        No results found
                      </Typography>
                    </Box>
                  ) : (
                    <ListingTable
                      items={getAllPatientRes?.totalPatient}
                      onRowClick={onRowClick}
                    />
                  )}

                  {getAllPatientRes?.loading && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 250,
                        width: '100%',
                      }}
                    >
                      <img
                        src={LoaderIcon}
                        alt="loader.."
                      />
                    </div>
                  )}
                </Box>
              </TableContainer>
            </Stack>
          </Card>
        </Stack>
      </Container>

      <Modal
        open={uploadCsvModal}
        data-testid={patientsTestId('upload-modal')}
        onClose={handleCloseUploadCsvModal}
        sx={{ p: 0 }}
      >
        <Card
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '55%',
            borderRadius: '8px',
            padding: '16px',
            maxHeight: '90%',
          }}
        >
          <CardHeader
            title="Upload Patients"
            sx={{
              padding: '16px',
              '& .MuiTypography-root': {
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                fontSize: '1.5rem',
                lineHeight: 1.2,
              },
            }}
          />
          <Divider />
          <CardContent sx={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
            <DropzoneArea
              filesLimit={1}
              onChange={(files: any) => setFileUploaded(files)}
              dropzoneText={
                <div>
                  <p>Drag & Drop or Click To Browse</p>
                  <Button>Browse</Button>
                </div>
              }
              showFileNames={true}
              Icon={(props: any) => null}
            />
            <Stack>
              <Button
                data-testid={patientsTestId('download-csv-template')}
                onClick={handleDownloadCSVTemplate}
              >
                <Typography
                  variant="h6"
                  color="#6366F1"
                  textAlign="center"
                >
                  Download Patients CSV Template
                </Typography>
              </Button>
            </Stack>
            <Stack>
              <Typography
                variant="h6"
                textAlign="center"
              >
                Enter patient information into the template, then re-upload the file.
              </Typography>
            </Stack>
            <Stack>
              <Typography
                variant="h6"
                textAlign="center"
              >
                Maximum upload file size: 10 MB.
              </Typography>
            </Stack>
          </CardContent>
          <Stack
            flexDirection="row"
            gap="1rem"
            justifyContent="flex-end"
            sx={{ marginTop: '1rem' }}
          >
            <Button
              color="inherit"
              data-testid={patientsTestId('upload-cancel')}
              onClick={handleCloseUploadCsvModal}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              data-testid={patientsTestId('upload-confirm')}
              onClick={handleUploadValidationTemplate}
            >
              Upload
            </Button>
          </Stack>
        </Card>
      </Modal>
      {getPatientExportCsvRespose?.isExportSuccess === true && (
        <SimpleSnackbar
          message="File Exported Successfully"
          isError={!getPatientExportCsvRespose?.isExportSuccess}
        />
      )}
    </>
  );
};

export default Patients;
